import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, MessageCircle } from 'lucide-react';
import LanguageSwitcher from '../common/LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container">
        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Company Logo and Title */}
          <div className="flex items-center justify-center mb-2">
            <NavLink to="/" className="flex items-center space-x-3">
              <svg 
                className="w-10 h-10" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke={isScrolled ? "#00afaf" : "#fff"} 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 5.5c1.5-2 4-2.5 5.5-1.5 1.5 1 2 3.5 1 6.5 0 0 .5 6.5-6.5 9-7-2.5-6.5-9-6.5-9-1-3-.5-5.5 1-6.5 1.5-1 4-.5 5.5 1.5Z"/>
              </svg>
              <span className={`text-2xl font-bold ${isScrolled ? 'text-primary-600' : 'text-white'}`}>
                Clinic Dental Bahia
              </span>
            </NavLink>
          </div>
          
          {/* Language Switcher and Menu Button */}
          <div className="flex items-center justify-between">
            <LanguageSwitcher isScrolled={isScrolled} />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          <NavLink to="/" className="flex items-center space-x-3">
            <svg 
              className="w-10 h-10" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={isScrolled ? "#00afaf" : "#fff"} 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 5.5c1.5-2 4-2.5 5.5-1.5 1.5 1 2 3.5 1 6.5 0 0 .5 6.5-6.5 9-7-2.5-6.5-9-6.5-9-1-3-.5-5.5 1-6.5 1.5-1 4-.5 5.5 1.5Z"/>
            </svg>
            <span className={`text-2xl font-bold ${isScrolled ? 'text-primary-600' : 'text-white'}`}>
              Clinic Dental Bahia
            </span>
          </NavLink>

          <nav className="flex items-center space-x-6">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `px-3 py-2 rounded-md transition-colors duration-200 ${
                  isActive ? 'bg-primary-100 text-primary-700' : 
                  isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:bg-white/10'
                }`
              }
            >
              {t('nav.home')}
            </NavLink>
            <NavLink 
              to="/services" 
              className={({ isActive }) => 
                `px-3 py-2 rounded-md transition-colors duration-200 ${
                  isActive ? 'bg-primary-100 text-primary-700' : 
                  isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:bg-white/10'
                }`
              }
            >
              {t('nav.services')}
            </NavLink>
            <NavLink 
              to="/about" 
              className={({ isActive }) => 
                `px-3 py-2 rounded-md transition-colors duration-200 ${
                  isActive ? 'bg-primary-100 text-primary-700' : 
                  isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:bg-white/10'
                }`
              }
            >
              {t('nav.about')}
            </NavLink>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => 
                `px-3 py-2 rounded-md transition-colors duration-200 ${
                  isActive ? 'bg-primary-100 text-primary-700' : 
                  isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:bg-white/10'
                }`
              }
            >
              {t('nav.contact')}
            </NavLink>
            <LanguageSwitcher isScrolled={isScrolled} />
            <a
              href="#appointment"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                isScrolled
                  ? 'bg-[#25D366] text-white hover:bg-[#20BD5C]'
                  : 'bg-[#25D366] text-white hover:bg-[#20BD5C]'
              }`}
            >
              <MessageCircle size={18} />
              <span>{t('nav.book')}</span>
            </a>
          </nav>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } overflow-hidden`}
        >
          <nav className="py-4 flex flex-col space-y-2">
            <NavLink
              to="/"
              className={({ isActive }) => 
                `px-4 py-2 rounded-md ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.home')}
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) => 
                `px-4 py-2 rounded-md ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.services')}
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) => 
                `px-4 py-2 rounded-md ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.about')}
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) => 
                `px-4 py-2 rounded-md ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.contact')}
            </NavLink>
            <a
              href="#appointment"
              className="flex items-center justify-center gap-2 mx-4 px-4 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#20BD5C] transition-colors font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              <MessageCircle size={20} />
              <span>{t('nav.book')}</span>
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;